import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { post } from '@rails/request.js'
import { toast } from 'sonner'
import { api_v1_emails_path } from '@/lib/routes'

type FormValues = {
  first_name: string
  last_name: string
  email: string
  inquiryType: 'support' | 'other'
  platform?: string // macOS, Windows, Linux
  message: string
}

export default function ContactForm() {
  const [inquiryType, setInquiryType] = useState<'support' | 'other'>('support')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const form = useForm<FormValues>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      inquiryType: 'support',
      platform: 'macos',
      message: '',
    },
    mode: 'onBlur',
  })

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true)

    try {
      const response = await post(api_v1_emails_path(), {
        body: JSON.stringify({
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          inquiryType: data.inquiryType || 'support',
          platform:
            data.inquiryType === 'support'
              ? data.platform || 'macos'
              : undefined,
          message: data.message,
        }),
        contentType: 'application/json',
        headers: {
          Accept: 'application/json',
        },
      })

      if (!response.ok) {
        const responseData = await response.json
        // Handle validation errors
        if (response.statusCode === 422 && responseData.errors) {
          const errorMessage = Array.isArray(responseData.errors)
            ? responseData.errors.join(', ')
            : responseData.errors
          toast.error(errorMessage)
        } else if (response.statusCode === 429) {
          toast.error(
            responseData.error || 'Too many requests. Please try again later.',
          )
        } else {
          toast.error(
            responseData.error ||
              'Something went wrong. Please try again later.',
          )
        }
        setIsSubmitting(false)
        return
      }

      // Success
      toast.success(
        'Thank you! Your message has been sent. We will get back to you soon.',
      )
      form.reset()
      setInquiryType('support')
      setIsSubmitting(false)
    } catch (error) {
      console.error('Error submitting form:', error)
      toast.error('Something went wrong. Please try again later.')
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-4">
          <div className="flex w-full flex-col gap-4 md:flex-row">
            <FormField
              control={form.control}
              name="first_name"
              rules={{
                required: 'First name is required',
              }}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="First name *" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="last_name"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="email"
            rules={{
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address',
              },
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="your@email.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="inquiryType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Reason for contact</FormLabel>
              <FormControl>
                <RadioGroup
                  className="flex flex-col space-y-1"
                  value={field.value}
                  onValueChange={(value: 'support' | 'other') => {
                    field.onChange(value)
                    setInquiryType(value)
                    // Reset platform when inquiry type changes
                    if (value === 'support') {
                      form.setValue('platform', 'macos')
                    } else {
                      form.setValue('platform', '')
                    }
                  }}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="support" id="support" />
                    <FormLabel htmlFor="support" className="font-normal">
                      Support Request
                    </FormLabel>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="other" id="other" />
                    <FormLabel htmlFor="other" className="font-normal">
                      Other Inquiry
                    </FormLabel>
                  </div>
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {inquiryType === 'support' && (
          <FormField
            control={form.control}
            name="platform"
            rules={{
              required: 'Platform is required for support requests',
            }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Platform</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value || 'macos'}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your platform" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="macos">macOS</SelectItem>
                    <SelectItem value="windows">Windows</SelectItem>
                    <SelectItem value="linux">Linux</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="message"
          rules={{
            required: 'Message is required',
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Please describe your inquiry or issue..."
                  // className="min-h-32"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Submit'}
        </Button>
      </form>
    </Form>
  )
}
